import React, { useState, useMemo, useEffect } from 'react';
import _flatten from 'lodash/flatten';
import SourceType from 'types/Source';
import { useDispatch } from 'react-redux';
import { useData } from 'resift';
import withSource from 'components/withSource/withSource';
import makeValidateMappings from 'fetches/makeValidateMappings';
import useExistingOrgMappingsInfo from 'components/Mappings/OrgMapping/useExistingOrgMappingsInfo';
import createMappingsCardData from './_helpers/createMappingsCardData';
import createDataBatchActions from './_helpers/createDataBatchActions';
import createLogisticalBatchActions from './_helpers/createLogisticalBatchActions';
import getSelectedDestinations from './_helpers/getSelectedDestinations';
import getValidatedMappingCards from './_helpers/getValidatedMappingCards';
import transformMappingsForValidation from './_helpers/transformMappingsForValidation';

import MappingCards from 'components/Mappings/MappingCards';
import MappingProps from 'types/Mapping';
import {
  PRIMARY_MAPPING_TYPE_KEY,
  POSITION_ID_MAPPING_TYPE_KEY,
  PROFILE_ATTR_MAPPING_TYPE_KEY,
} from 'pages/Directory/consts';

interface Props {
  directory: any;
  sourceMappings: MappingProps[];
  userFields: any;
  sourceFromProps: SourceType;
  setMappings: (batchActions: any[]) => void;
  setDirty: (dirty: boolean) => void;
}

function Mappings({
  directory,
  sourceFromProps,
  sourceMappings,
  userFields,
  setMappings,
  setDirty,
}: Props) {
  const dispatch = useDispatch();
  const validateMappings = makeValidateMappings(sourceFromProps.id);
  const validationResponse = useData(validateMappings);
  const { validationResults } = validationResponse;
  const existingMappingsInfo = useExistingOrgMappingsInfo(directory, sourceFromProps.id);
  const { orgMappingsSet } = existingMappingsInfo;
  const initialState = useMemo(
    () => createMappingsCardData(sourceMappings, validationResults, userFields),
    [sourceMappings, validationResults],
  );

  const [_mappingCards, setMappingSections] = useState(initialState);
  const mappingCards = getValidatedMappingCards(_mappingCards, validationResults);
  const selectedDestinations = useMemo(() => getSelectedDestinations(mappingCards), [mappingCards]);

  useEffect(() => {
    const batchActions = buildBatchActions([], sourceMappings, sourceFromProps.id, orgMappingsSet);
    setMappings(batchActions);
  }, []);

  const handleUpdateState = (cardIndex: number, newMappings: any[]) => {
    setDirty(true);

    const newState = [...mappingCards];
    newState.splice(cardIndex, 1, {
      ...newState[cardIndex],
      mappings: newMappings,
    });

    const batchActions = buildBatchActions(
      newState,
      sourceMappings,
      sourceFromProps.id,
      orgMappingsSet,
    );
    setMappings(batchActions);
    setMappingSections(newState);

    const _latestMappings = newState.map(s => s.mappings);
    const latestMappings = transformMappingsForValidation(_flatten(_latestMappings));
    dispatch(validateMappings(latestMappings));
  };

  return (
    <MappingCards
      cardData={mappingCards}
      selectedDestinations={selectedDestinations}
      onUpdateState={handleUpdateState}
      sourceFromProp={sourceFromProps}
    />
  );
}

export default withSource(Mappings);

function buildBatchActions(
  mappingCards: any,
  sourceMappings: any,
  sourceId: any,
  orgMappingsSet: boolean,
) {
  const logisticalBatchActions = createLogisticalBatchActions(
    mappingCards,
    sourceMappings,
    sourceId,
    orgMappingsSet,
  );

  let dataMappings = [];
  if (mappingCards.length) {
    dataMappings = mappingCards.reduce((acc: any, { initialMappings, mappings, type }: any) => {
      if (
        type === PRIMARY_MAPPING_TYPE_KEY ||
        type === POSITION_ID_MAPPING_TYPE_KEY ||
        type === PROFILE_ATTR_MAPPING_TYPE_KEY
      ) {
        const batchActions = createDataBatchActions(initialMappings, mappings, sourceId);
        return [...acc, ...batchActions];
      }

      return acc;
    }, []);
  } else {
    dataMappings = createDataBatchActions([], sourceMappings, sourceId);
  }

  return [...logisticalBatchActions, ...dataMappings];
}
